import React from 'react';
import { styled } from 'styled-components';
import locationPin from '@ingka/ssr-icon/paths/location-pin';
import clipboardCheckmark from '@ingka/ssr-icon/paths/clipboard-checkmark';
import calendar from '@ingka/ssr-icon/paths/calendar';
import moneyCash from '@ingka/ssr-icon/paths/money-cash';
import SSRIcon from '@ingka/ssr-icon';
import Text from '@ingka/text';
import Price from '@ingka/price';
import designTokens from '@ingka/variables/design-tokens';
import { useTranslation } from 'react-i18next';
import { type CustomerPrice } from '../services/appointment/model';
import { mediaQueries } from '../styles/mediaQueries';
import {
  isServiceLocationStore,
  getServiceLocationKey,
} from '../helpers/appHelper';
import { useStateSelector } from '../store/';
import { formatDate } from '../helpers/dateHelper';
import {
  selectSelectedTimeSlot,
  selectServiceForTheSelectedTimeSlot,
  selectStoreForTheSelectedTimeSlot,
} from '../reducers/selectors';
import { getLocale } from '../helpers/localeHelper';
const StyledUl = styled.ul`
  list-style-type: none;
  margin: 0.5rem 0;
  padding: 0;

  ${mediaQueries.portraitTabletUp} {
    display: flex;
  }
`;

const StyledLi = styled.li`
  margin: 0.675rem 0;
  display: flex;
  align-items: flex-start;

  & > svg {
    margin-right: 0.5rem;
    flex-shrink: 0;
  }

  ${mediaQueries.portraitTabletUp} {
    margin: 0;

    &:not(:first-child) {
      margin-left: 2rem;
    }
  }
`;

const StyledParagraph = styled(Text)`
  &&& {
    line-height: 1.5;
    margin: 0;
    span {
      font-weight: ${designTokens.fontWeightRegular};
    }
  }
`;

interface PriceDetailsProps {
  customerPrice: CustomerPrice;
}

const PriceDetails: React.FC<PriceDetailsProps> = ({ customerPrice }) => {
  const { t } = useTranslation();
  const { priceFormatted } = customerPrice;
  const priceLabel =
    customerPrice.price > 0
      ? {
          mainPrice: (
            <Price
              {...priceFormatted.mainPrice}
              fixedSize
              size="small"
              subtle
            />
          ),
          secondaryPrice: priceFormatted.secondaryPrice && (
            <Price
              {...priceFormatted.secondaryPrice}
              fixedSize
              size="small"
              subtle
            />
          ),
        }
      : { mainPrice: t('pages.contact.freePrice') };

  return (
    <StyledParagraph tagName="span" textStyle="Body.L">
      {priceLabel.mainPrice}
      {priceLabel.secondaryPrice && ' '}
      {priceLabel.secondaryPrice && priceLabel.secondaryPrice}
    </StyledParagraph>
  );
};

const SelectedDetails: React.FC = () => {
  const { t } = useTranslation();
  const locale = getLocale();
  const store = useStateSelector(selectStoreForTheSelectedTimeSlot);
  const timeSlot = useStateSelector(selectSelectedTimeSlot);
  const service = useStateSelector(selectServiceForTheSelectedTimeSlot);

  const getTimeSlotText = (): string => {
    return timeSlot?.dateFrom && timeSlot?.dateTo && service?.location
      ? formatDate(
          timeSlot.dateFrom,
          timeSlot.dateTo,
          service.location,
          locale,
          t,
          CSS_CONFIG,
        )
      : '';
  };

  if (!service) {
    return null;
  }
  return (
    <StyledUl>
      <StyledLi data-cy="selected-details-service-name">
        <SSRIcon
          paths={clipboardCheckmark}
          ariaHidden={false}
          role="img"
          aria-label={t('pages.contact.serviceDetails.serviceName')}
        />
        <StyledParagraph tagName="p" textStyle="Body.L">
          {t(`services.name.${service.serviceId}`)}
        </StyledParagraph>
      </StyledLi>
      <StyledLi data-cy="selected-details-service-price">
        <SSRIcon
          paths={moneyCash}
          ariaHidden={false}
          role="img"
          aria-label={t('pages.contact.serviceDetails.price')}
        />
        <PriceDetails customerPrice={service.customerPrice} />
      </StyledLi>
      <StyledLi data-cy="selected-details-location">
        <SSRIcon
          paths={locationPin}
          ariaHidden={false}
          role="img"
          aria-label={t('pages.contact.serviceDetails.location')}
        />
        {isServiceLocationStore(service.location) ? (
          <StyledParagraph
            tagName="p"
            textStyle="Body.L"
            data-cy="selected-details-store-name"
          >
            {store?.text || ''}
          </StyledParagraph>
        ) : (
          <StyledParagraph
            tagName="p"
            textStyle="Body.L"
            data-cy={`selected-details-location-type-${getServiceLocationKey(
              service.location,
            )}`}
          >
            {t(
              `components.servicePicker.${getServiceLocationKey(
                service.location,
              )}`,
            )}
          </StyledParagraph>
        )}
      </StyledLi>
      <StyledLi data-cy="selected-details-service-time">
        <SSRIcon
          paths={calendar}
          ariaHidden={false}
          role="img"
          aria-label={t('pages.contact.serviceDetails.dateAndTime')}
        />
        <StyledParagraph tagName="p" textStyle="Body.L">
          {getTimeSlotText()}
        </StyledParagraph>
      </StyledLi>
    </StyledUl>
  );
};

export default SelectedDetails;
